import { closestCenter, DndContext, PointerSensor, TouchSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BedDouble, GripVertical, Moon, Plus, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ItemKind, ItineraryItemDTO } from "@shared/types";
import { addDays } from "@shared/types";
import { dateTimeIn, durationText, fmtDay, localDateIn, timeIn, weekday } from "@/lib/format";
import { CATEGORY, ITEM_KIND, ITEM_STATUS, MODE } from "@/lib/meta";
import { toast, useUI } from "@/lib/store";
import { applyChange, queryClient, tripApi, type Bundle, type DayInfo, type Derived } from "@/lib/trip";
import { Badge, Button, cx, Empty, Menu, MenuItem } from "../ui";
import { ItemEditor } from "./ItemEditor";

function ItemRow({ it, d, color, dragHandle, onOpen }: { it: ItineraryItemDTO; d: Derived; color: string; dragHandle?: React.ReactNode; onOpen: () => void }) {
  const p = it.placeId ? d.places.get(it.placeId) : null;
  const selectedItemId = useUI((s) => s.selectedItemId);
  const set = useUI((s) => s.set);
  const emoji = it.kind === "transport" ? (MODE[it.mode ?? "other"]?.emoji ?? "➡️") : it.kind === "visit" && p ? CATEGORY[p.category].emoji : ITEM_KIND[it.kind].emoji;
  let time = it.startTime;
  let sub: string | null = null;
  if (it.kind === "transport") {
    const dep = timeIn(it.departAt, it.fromTz);
    const arrDate = it.arriveAt ? localDateIn(it.arriveAt, it.toTz) : null;
    const arr = arrDate && arrDate !== it.day ? dateTimeIn(it.arriveAt, it.toTz) : timeIn(it.arriveAt, it.toTz);
    time = dep ?? it.startTime;
    sub = `${it.fromName ?? "?"} → ${it.toName ?? "?"}${arr ? ` · приб. ${arr}` : it.durationMin ? ` · ~${durationText(it.durationMin)}` : ""}`;
  } else if (it.kind === "stay") sub = `${it.nights ?? 1} ${(it.nights ?? 1) === 1 ? "ночь" : (it.nights ?? 1) < 5 ? "ночи" : "ночей"}${p ? ` · ${p.name}` : ""}`;
  else if (p && p.name !== it.title) sub = p.name;
  else if (it.durationMin) sub = durationText(it.durationMin);
  return (
    <div
      className={cx(
        "group flex items-start gap-2 rounded-xl px-2 py-2 transition hover:bg-[var(--surface-2)]",
        selectedItemId === it.id && "bg-[var(--accent-soft)]",
        it.status === "cancelled" && "opacity-50",
      )}
      onMouseEnter={() => p && set({ hoverPlaceId: p.id })}
      onMouseLeave={() => p && set({ hoverPlaceId: null })}
    >
      {dragHandle}
      <button className="flex min-w-0 flex-1 items-start gap-2.5 text-left" onClick={onOpen}>
        <span className="grid size-8 shrink-0 place-items-center rounded-full text-[15px]" style={{ background: `color-mix(in srgb, ${color} 16%, transparent)` }}>
          {emoji}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-1.5">
            {time && <span className="shrink-0 text-[12px] font-bold tabular-nums text-muted">{time}</span>}
            <span className={cx("min-w-0 truncate text-[14px] font-semibold", it.kind === "note" && "font-medium italic")}>{it.title}</span>
          </span>
          {sub && <span className="block truncate text-[12px] text-muted">{sub}</span>}
          {it.notes && it.kind !== "note" && <span className="mt-0.5 line-clamp-1 block text-[12px] text-muted">{it.notes}</span>}
        </span>
        {it.status !== "planned" && <Badge className={cx("mt-1 shrink-0", ITEM_STATUS[it.status].tone)}>{ITEM_STATUS[it.status].label}</Badge>}
      </button>
    </div>
  );
}

function SortableItem(props: { it: ItineraryItemDTO; d: Derived; color: string; onOpen: () => void; canEdit: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.it.id, disabled: !props.canEdit });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={cx(isDragging && "relative z-10 rounded-xl bg-[var(--surface-solid)] shadow-lg")}>
      <ItemRow
        {...props}
        dragHandle={
          props.canEdit ? (
            <span {...attributes} {...listeners} className="mt-1.5 cursor-grab touch-none text-muted opacity-40 group-hover:opacity-100" aria-label="Перетащить">
              <GripVertical size={16} />
            </span>
          ) : (
            <span className="w-1" />
          )
        }
      />
    </div>
  );
}

function DaySection({
  info,
  b,
  d,
  selected,
  onAdd,
  onOpen,
}: {
  info: DayInfo;
  b: Bundle;
  d: Derived;
  selected: boolean;
  onAdd: (day: string, kind: ItemKind) => void;
  onOpen: (it: ItineraryItemDTO) => void;
}) {
  const selectDay = useUI((s) => s.selectDay);
  const canEdit = b.trip.myRole !== "viewer";
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }));
  const [order, setOrder] = useState(info.items.map((x) => x.id));
  useEffect(() => setOrder(info.items.map((x) => x.id)), [info.items]);
  const byId = new Map(info.items.map((x) => [x.id, x]));

  // что «перетекает» из прошлых дней: прибытие ночного переезда и продолжение ночёвки
  const carry = useMemo(() => {
    const out: { key: string; text: string; icon: React.ReactNode }[] = [];
    const prev = d.dayIndex.get(addDays(info.day, -1));
    for (const it of prev?.items ?? []) {
      if (it.kind === "transport" && it.arriveAt && localDateIn(it.arriveAt, it.toTz) === info.day) {
        out.push({ key: it.id, text: `Прибытие ${timeIn(it.arriveAt, it.toTz)} — ${it.title}`, icon: <Moon size={14} /> });
      }
    }
    if (info.night && info.night.day !== info.day) {
      const n = Math.round((Date.parse(info.day) - Date.parse(info.night.day)) / 86400000) + 1;
      out.push({ key: `n-${info.night.id}`, text: `${info.night.title} (ночь ${n} из ${info.night.nights ?? 1})`, icon: <BedDouble size={14} /> });
    }
    return out;
  }, [d, info]);

  const onDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const next = arrayMove(order, order.indexOf(String(e.active.id)), order.indexOf(String(e.over.id)));
    setOrder(next);
    next.forEach((id, i) => {
      const it = byId.get(id);
      if (it) applyChange(queryClient, b.trip.id, "item", "upsert", id, { ...it, position: i + 1 });
    });
    try {
      await tripApi.reorder(b.trip.id, info.day, next);
    } catch (err) {
      toast((err as Error).message, { tone: "error" });
    }
  };

  return (
    <section data-plan-day={info.day} className={cx("rounded-2xl border transition", selected ? "border-[var(--border-strong)] bg-[var(--surface-solid)] shadow-sm" : "border-transparent")}>
      <div className="flex items-center gap-2 px-2 pt-2">
        <button className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-1 py-1 text-left" onClick={() => selectDay(selected ? null : info.day)}>
          <span className="grid size-9 shrink-0 place-items-center rounded-xl text-white" style={{ background: info.color }}>
            <span className="font-display text-[14px] leading-none font-bold">{info.index + 1}</span>
          </span>
          <span className="min-w-0">
            <span className="block text-[14px] font-extrabold">
              {weekday(info.day)}, {fmtDay(info.day)}
            </span>
            <span className="block truncate text-[12px] text-muted">
              {info.base ?? "—"}
              {info.night && ` · 🛏 ${info.night.title.replace(/^Ночёвка:\s*/, "")}`}
            </span>
          </span>
        </button>
        {canEdit && (
          <Menu
            trigger={(t) => (
              <button onClick={t} className="grid size-8 place-items-center rounded-lg text-muted hover:bg-[var(--surface-2)]" aria-label="Добавить в день">
                <Plus size={17} />
              </button>
            )}
          >
            {(close) =>
              (["visit", "activity", "stay", "transport", "note"] as ItemKind[]).map((k) => (
                <MenuItem
                  key={k}
                  onClick={() => {
                    close();
                    onAdd(info.day, k);
                  }}
                >
                  {ITEM_KIND[k].emoji} {ITEM_KIND[k].label}
                </MenuItem>
              ))
            }
          </Menu>
        )}
      </div>
      <div className="px-1 pb-2">
        {carry.map((c) => (
          <div key={c.key} className="ml-2 flex items-center gap-2 px-2 py-1 text-[12px] text-muted">
            {c.icon}
            <span className="truncate">{c.text}</span>
          </div>
        ))}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {order.map((id) => {
              const it = byId.get(id);
              return it ? <SortableItem key={id} it={it} d={d} color={info.color} onOpen={() => onOpen(it)} canEdit={canEdit} /> : null;
            })}
          </SortableContext>
        </DndContext>
        {!info.items.length && !carry.length && <div className="px-4 py-1 text-[13px] text-muted">Пока пусто</div>}
      </div>
    </section>
  );
}

export function PlanPanel({ b, d }: { b: Bundle; d: Derived }) {
  const selectedDay = useUI((s) => s.selectedDay);
  const selectedItemId = useUI((s) => s.selectedItemId);
  const set = useUI((s) => s.set);
  const [editor, setEditor] = useState<{ item?: ItineraryItemDTO; preset?: { kind: ItemKind; day: string } } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedDay) return;
    listRef.current?.querySelector(`[data-plan-day="${selectedDay}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedDay]);

  useEffect(() => {
    if (!selectedItemId) return;
    const it = b.items.find((x) => x.id === selectedItemId);
    if (it && !editor) setEditor({ item: it });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId]);

  const open = (it: ItineraryItemDTO) => {
    set({ selectedItemId: it.id });
    const p = it.placeId ? d.places.get(it.placeId) : null;
    if (p) useUI.getState().focusMap({ bounds: [[p.lat, p.lng], [p.lat, p.lng]] });
    setEditor({ item: it });
  };

  if (!b.items.length && !b.places.length) {
    return (
      <Empty icon="🗺️" title="План пока пуст">
        Найдите место через поиск на карте, вставьте ссылку на YouTube во вкладке «Материалы» или попросите ИИ набросать маршрут.
      </Empty>
    );
  }

  return (
    <div ref={listRef} className="space-y-1.5 pb-6">
      {b.trip.myRole !== "viewer" && (
        <Button variant="soft" className="mb-2 w-full" icon={<Sparkles size={16} />} onClick={() => setEditor({ preset: { day: selectedDay ?? d.days[0]?.day ?? b.trip.startDate, kind: "visit" } })}>
          Добавить в план — словами или ссылкой
        </Button>
      )}
      {d.outside.length > 0 && (
        <div className="rounded-2xl border border-amber-300/60 bg-amber-50 p-3 text-[13px] dark:bg-amber-400/10">
          <b>Вне дат поездки: {d.outside.length}</b>
          {d.outside.map((it) => (
            <button key={it.id} className="block w-full truncate text-left hover:underline" onClick={() => open(it)}>
              {it.day} — {it.title}
            </button>
          ))}
        </div>
      )}
      {d.days.map((info) => (
        <DaySection
          key={info.day}
          info={info}
          b={b}
          d={d}
          selected={selectedDay === info.day}
          onAdd={(day, kind) => setEditor({ preset: { day, kind } })}
          onOpen={open}
        />
      ))}
      {editor && (
        <ItemEditor
          b={b}
          d={d}
          open
          item={editor.item ? (b.items.find((x) => x.id === editor.item!.id) ?? editor.item) : null}
          preset={editor.preset ?? null}
          onClose={() => {
            setEditor(null);
            set({ selectedItemId: null });
          }}
        />
      )}
    </div>
  );
}
